import React, { useMemo } from 'react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';

import dataTypes from '../../../constants/dataTypes';
import { get } from '../../../utils/arrayUtil';
import { getFormattedPhoneNumber } from '../../../utils/formatPhoneUtil';
import { getDisplayValueForType } from '../../../utils/tableUtil';
import RequirementBlock from '../../CertificateCreationFlow/components/RequirementBlock';
import InsuranceDocumentBlock from '../InsuranceDocumentBlock';
import InsuranceLanguageBlock from '../InsuranceLanguageBlock';
import BooleanRequirements from '../InsurancePolicyBlock/BooleanRequirements';
import InsurancePolicyCard from '../InsurancePolicyBlock/InsurancePolicyCard';
import InsurancePolicyRequirements from '../InsurancePolicyBlock/InsurancePolicyRequirements';
import NonbooleanRequirements from '../InsurancePolicyBlock/NonbooleanRequirements';
import RequirementBlockCardHeader from '../RequirementBlockCardHeader';

const AllRequirements = ({
  documentRequirements,
  minimumRequirements,
  languageRequirements,
  questionRequirements = [],
  companyContact = () => {}
}) => {
  const { docTypeQuestionMap, policyTypeQuestionMap } = useMemo(() => {
    const docMap = {};
    const polMap = {};

    questionRequirements.forEach(qR => {
      qR?.links?.forEach(link => {
        if (link.resourceType === 'insurance_document_type') {
          (docMap[link.resourceId] ||= []).push(qR);
        }

        if (link.resourceType === 'insurance_policy_type') {
          (polMap[link.resourceId] ||= []).push(qR);
        }
      });
    });

    return { docTypeQuestionMap: docMap, policyTypeQuestionMap: polMap };
  }, [questionRequirements]);

  return (
    <Flex flexBasis="100%" flexFlow="row wrap" justifyContent="space-between" mt={8}>
      <Flex flexBasis="48%" flexFlow="column">
        {companyContact()}
        <RequirementBlock title="Required Documents">
          {documentRequirements.length < 1
            ? <Text fontStyle="italics">None</Text>
            : documentRequirements.map(docReq => (
              <InsuranceDocumentBlock
                description={docReq.insuranceDocumentTypeDescription}
                displayName={docReq.insuranceDocumentTypeDisplayName}
                isRequired={docReq.required}
                key={`document.${docReq.id}`}
                relatedQuestions={docTypeQuestionMap[docReq.insuranceDocumentTypeId] || []}
              />
            ))}
        </RequirementBlock>

        <RequirementBlock title="Required Language">
          {languageRequirements.length < 1
            ? <Text fontStyle="italics">None</Text>
            : languageRequirements.map(({ insuranceLanguage }) => (
              <InsuranceLanguageBlock
                description={insuranceLanguage.language}
                displayName={insuranceLanguage.displayName}
                isRequired={false}
                key={`language.${insuranceLanguage.id}`}
              />
            ))}
        </RequirementBlock>
      </Flex>
      <Flex flexBasis="48%">
        <RequirementBlock title="Required Coverage">
          {Object.values(minimumRequirements).map(req => {
            const properties = Object.values(req.policyProperties);

            const booleanProperties = properties
              .filter(p => p.propertyDataType === dataTypes.Boolean)
              .map(prop => {
                if (get(prop, 'requiredValue', 0) > 0) {
                  return (
                    <BooleanRequirements displayName={prop.propertyDisplayName} key={prop.id} />
                  );
                }
              });

            const nonBooleanProperties = properties
              .filter(p => p.propertyDataType !== dataTypes.Boolean)
              .map(prop => {
                const propertyName = prop.propertyDisplayName;
                const propertyValue = getDisplayValueForType(prop.requiredValue, prop.propertyDataType) ?? '-';
                return (
                  <NonbooleanRequirements
                    key={prop.id}
                    propertyName={propertyName}
                    propertyValue={propertyValue}
                  />
                );
              });
            return (
              <InsurancePolicyCard key={req.policyTypeId}>
                <RequirementBlockCardHeader
                  displayName={req.policyTypeDisplayName}
                  isRequired={req.required}
                  relatedQuestions={policyTypeQuestionMap[req.policyTypeId] || []}
                />
                <InsurancePolicyRequirements
                  booleanProperties={booleanProperties}
                  nonBooleanProperties={nonBooleanProperties}
                />
              </InsurancePolicyCard>
            );
          })}
        </RequirementBlock>
      </Flex>
    </Flex>
  );
};

export default AllRequirements;
